// src/pages/Dashboard/Admin/PagesPersonnalisees/utils/pagesDashboardUtils.js

export const PAGE_FALLBACK_IMAGE = "/img/no-image.jpg";

export const DEFAULT_PAGE_FILTERS = {
  query: "",
  status: "",
  sort: "updated_desc",
};

export const PAGE_SORT_OPTIONS = [
  { value: "updated_desc", label: "Dernière modification" },
  { value: "title_asc", label: "Titre A-Z" },
  { value: "title_desc", label: "Titre Z-A" },
  { value: "slug_asc", label: "Chemin A-Z" },
  { value: "active_first", label: "Actives en premier" },
];

export const QUILL_MODULES = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
};

export const QUILL_FORMATS = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function slugify(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value = "", max = 180) {
  const text = stripHtml(value || "");

  if (text.length <= max) return text;

  return `${text.slice(0, max).trim()}…`;
}

export function formatDateFR(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).format(date);
}

export function getPageTitle(page) {
  return page?.title || page?.titre || "Sans titre";
}

export function getPageSlug(page) {
  return page?.slug || "";
}

export function getPageIntroduction(page) {
  return page?.introduction || page?.description || "";
}

export function isPageActive(page) {
  return Boolean(page?.is_active ?? page?.actif);
}

export function getPageArticlesCount(page) {
  const possibleLists = [
    page?.articles,
    page?.Articles,
    page?.articles_pages,
    page?.ArticlesPages,
    page?.pageArticles,
  ];

  const found = possibleLists.find(Array.isArray);

  return found ? found.length : Number(page?.articlesCount || page?.articleCount || 0);
}

export function getPageUpdatedAt(page) {
  return page?.updated_at || page?.updatedAt || page?.created_at || page?.createdAt;
}

export function getDefaultPageForm() {
  return {
    titre: "",
    slug: "",
    introduction: "",
    actif: true,
  };
}

export function mapPageToForm(page) {
  if (!page) return getDefaultPageForm();

  return {
    titre: getPageTitle(page),
    slug: getPageSlug(page),
    introduction: getPageIntroduction(page),
    actif: isPageActive(page),
  };
}

export function validatePageForm(formData) {
  const errors = {};

  if (!String(formData.titre || "").trim()) {
    errors.titre = "Le titre de la page est requis.";
  }

  if (!String(formData.slug || "").trim()) {
    errors.slug = "Le chemin de la page est requis.";
  }

  return errors;
}

export function buildPagePayload(formData, id = null) {
  const payload = {
    titre: String(formData.titre || "").trim(),
    slug: slugify(formData.slug),
    introduction: formData.introduction || "",
    actif: !!formData.actif,
  };

  if (id) payload.id = id;

  return payload;
}

export function sortPages(pages, sort = "updated_desc") {
  const rows = safeArray(pages).slice();

  return rows.sort((a, b) => {
    const titleA = getPageTitle(a);
    const titleB = getPageTitle(b);
    const slugA = getPageSlug(a);
    const slugB = getPageSlug(b);

    if (sort === "title_asc") {
      return titleA.localeCompare(titleB, "fr");
    }

    if (sort === "title_desc") {
      return titleB.localeCompare(titleA, "fr");
    }

    if (sort === "slug_asc") {
      return slugA.localeCompare(slugB, "fr");
    }

    if (sort === "active_first") {
      const activeA = isPageActive(a) ? 1 : 0;
      const activeB = isPageActive(b) ? 1 : 0;

      if (activeA !== activeB) return activeB - activeA;

      return titleA.localeCompare(titleB, "fr");
    }

    const dateA = new Date(getPageUpdatedAt(a) || 0).getTime();
    const dateB = new Date(getPageUpdatedAt(b) || 0).getTime();

    if (dateA !== dateB) return dateB - dateA;

    return titleA.localeCompare(titleB, "fr");
  });
}

export function filterPages(pages, filters) {
  const query = normalizeText(filters?.query || "");
  const status = filters?.status || "";

  return sortPages(pages, filters?.sort).filter((page) => {
    const searchable = normalizeText(
      `${getPageTitle(page)} ${getPageSlug(page)} ${stripHtml(
        getPageIntroduction(page)
      )}`
    );

    const active = isPageActive(page);

    const matchQuery = !query || searchable.includes(query);

    const matchStatus =
      !status ||
      (status === "active" ? active : !active);

    return matchQuery && matchStatus;
  });
}

export function calculatePagesStats(pages) {
  const rows = safeArray(pages);

  let active = 0;
  let inactive = 0;
  let withIntro = 0;
  let withoutIntro = 0;
  let withArticles = 0;
  let totalArticles = 0;

  for (const page of rows) {
    if (isPageActive(page)) active += 1;
    else inactive += 1;

    if (stripHtml(getPageIntroduction(page))) withIntro += 1;
    else withoutIntro += 1;

    const articlesCount = getPageArticlesCount(page);
    totalArticles += articlesCount;

    if (articlesCount > 0) withArticles += 1;
  }

  return {
    total: rows.length,
    active,
    inactive,
    withIntro,
    withoutIntro,
    withArticles,
    withoutArticles: Math.max(0, rows.length - withArticles),
    totalArticles,
  };
}

export function getDefaultArticleForm(nextOrder = 1) {
  return {
    titre_article: "",
    contenu_texte: "",
    url_image: "",
    ordre: nextOrder,
  };
}

export function mapArticleToForm(article, nextOrder = 1) {
  if (!article) return getDefaultArticleForm(nextOrder);

  return {
    titre_article: article?.titre_article || "",
    contenu_texte: article?.contenu_texte || "",
    url_image: article?.url_image || "",
    ordre: article?.ordre ?? nextOrder,
  };
}

export function buildArticlePayload(formData, pageId, id = null) {
  const payload = {
    page_id: pageId,
    titre_article: String(formData.titre_article || "").trim(),
    contenu_texte: formData.contenu_texte || "",
    url_image: String(formData.url_image || "").trim(),
    ordre: formData.ordre === "" ? 0 : Number(formData.ordre),
  };

  if (id) payload.id = id;

  return payload;
}

export function sortPageArticles(articles) {
  return safeArray(articles)
    .slice()
    .sort((a, b) => {
      const orderA = Number(a?.ordre || 0);
      const orderB = Number(b?.ordre || 0);

      if (orderA !== orderB) return orderA - orderB;

      return String(a?.titre_article || "").localeCompare(
        String(b?.titre_article || ""),
        "fr"
      );
    });
}